import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchAccountingPeriods, closeAccountingPeriod, reopenAccountingPeriod,
  generateAccountingPeriods, fetchGLConfig, updateGLConfig,
} from '../api';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

export default function AccountingPeriods({ authAxios }) {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genYear, setGenYear] = useState(new Date().getFullYear());
  const [glConfig, setGlConfig] = useState(null);
  const [fyMonth, setFyMonth] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  const [yearEndTarget, setYearEndTarget] = useState(null);

  const loadPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAccountingPeriods(authAxios);
      setPeriods(Array.isArray(res) ? res : res.results || []);
    } catch (e) {
      console.error('Failed to load periods', e);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const loadConfig = useCallback(async () => {
    try {
      const cfg = await fetchGLConfig(authAxios);
      setGlConfig(cfg);
      setFyMonth(cfg.fiscal_year_start_month);
    } catch (e) {
      console.error('Failed to load GL config', e);
    }
  }, [authAxios]);

  useEffect(() => { loadPeriods(); loadConfig(); }, [loadPeriods, loadConfig]);

  const handleClose = async (id, yearEndClose = false) => {
    setActionLoading(id);
    setYearEndTarget(null);
    try {
      await closeAccountingPeriod(authAxios, id, yearEndClose ? { year_end_close: true } : {});
      await loadPeriods();
    } catch (e) {
      console.error('Close failed', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReopen = async (id) => {
    setActionLoading(id);
    try {
      await reopenAccountingPeriod(authAxios, id);
      await loadPeriods();
    } catch (e) {
      console.error('Reopen failed', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerate = async () => {
    setActionLoading('gen');
    try {
      await generateAccountingPeriods(authAxios, genYear);
      await loadPeriods();
    } catch (e) {
      console.error('Generate failed', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveConfig = async () => {
    setActionLoading('cfg');
    try {
      const updated = await updateGLConfig(authAxios, { fiscal_year_start_month: fyMonth });
      setGlConfig(updated);
    } catch (e) {
      console.error('Config save failed', e);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Accounting Periods</h2>

      {/* GL Config */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
        <h3 className="font-semibold text-gray-700">Fiscal Year Configuration</h3>
        <div className="flex gap-4 items-end">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Fiscal Year Start Month</label>
            <select value={fyMonth} onChange={(e) => setFyMonth(Number(e.target.value))}
              className="border rounded px-3 py-1.5 text-sm">
              {MONTHS.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <button onClick={handleSaveConfig} disabled={actionLoading === 'cfg'}
            className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
            {actionLoading === 'cfg' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Generate Periods */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
        <h3 className="font-semibold text-gray-700">Generate Monthly Periods</h3>
        <div className="flex gap-4 items-end">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Year</label>
            <input type="number" value={genYear} onChange={(e) => setGenYear(Number(e.target.value))}
              className="border rounded px-3 py-1.5 text-sm w-28" min={2000} max={2100} />
          </div>
          <button onClick={handleGenerate} disabled={actionLoading === 'gen'}
            className="px-4 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50">
            {actionLoading === 'gen' ? 'Generating…' : 'Generate 12 Periods'}
          </button>
        </div>
      </div>

      {/* Period List */}
      {loading ? (
        <p className="text-gray-500">Loading periods…</p>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="px-4 py-2">Period</th>
              <th className="px-4 py-2">Start</th>
              <th className="px-4 py-2">End</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Closed At</th>
              <th className="px-4 py-2">Closed By</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{p.name}</td>
                <td className="px-4 py-2">{p.start_date}</td>
                <td className="px-4 py-2">{p.end_date}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    p.status === 'CLOSED' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {p.status_display || p.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">{p.closed_at || '—'}</td>
                <td className="px-4 py-2 text-xs text-gray-500">{p.closed_by_name || '—'}</td>
                <td className="px-4 py-2">
                  {p.status === 'OPEN' ? (
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleClose(p.id)} disabled={actionLoading === p.id}
                        className="px-3 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600 disabled:opacity-50">
                        {actionLoading === p.id ? '…' : 'Close'}
                      </button>
                      {yearEndTarget === p.id ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleClose(p.id, true)} disabled={actionLoading === p.id}
                            className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50">
                            Confirm Year-End
                          </button>
                          <button onClick={() => setYearEndTarget(null)} className="px-2 py-1 text-gray-500 text-xs hover:text-gray-700">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setYearEndTarget(p.id)} title="Year-End Close: closes revenue/expense to retained earnings"
                          className="px-2 py-1 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50">
                          Year-End
                        </button>
                      )}
                    </div>
                  ) : (
                    <button onClick={() => handleReopen(p.id)} disabled={actionLoading === p.id}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-600 disabled:opacity-50">
                      {actionLoading === p.id ? '…' : 'Reopen'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {periods.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No periods found. Generate periods above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
